/**
 * Isomorphic HMAC-SHA256 signature generation
 * Works in both Node.js and browser environments
 */

/**
 * Check if Web Crypto API is available
 * Works in browsers and Node.js 18+
 */
function hasWebCrypto(): boolean {
	return typeof crypto !== "undefined" && crypto.subtle != null;
}

/**
 * Create HMAC-SHA256 signature using Web Crypto API
 * Preferred method - works in browsers and Node.js 18+
 */
async function createSignatureWebCrypto(
	secret: string,
	timestamp: string,
	body: string,
): Promise<string> {
	const encoder = new TextEncoder();
	const keyData = encoder.encode(secret);
	const message = encoder.encode(`${timestamp}.${body}`);

	const key = await crypto.subtle.importKey(
		"raw",
		keyData,
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"],
	);

	const signatureBuffer = await crypto.subtle.sign("HMAC", key, message);

	return Array.from(new Uint8Array(signatureBuffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}

/**
 * Create HMAC-SHA256 signature using Node.js crypto
 * Fallback for older Node.js versions (< 18)
 */
async function createSignatureNode(
	secret: string,
	timestamp: string,
	body: string,
): Promise<string> {
	const { createHmac } = await import("node:crypto");
	return createHmac("sha256", secret)
		.update(`${timestamp}.${body}`)
		.digest("hex");
}

/**
 * Create HMAC-SHA256 signature for SDK requests
 * Prefers Web Crypto API (browsers + Node 18+), falls back to node:crypto
 */
export async function createSignature(
	secret: string,
	timestamp: string,
	body: string,
): Promise<string> {
	if (hasWebCrypto()) {
		return createSignatureWebCrypto(secret, timestamp, body);
	}
	return createSignatureNode(secret, timestamp, body);
}
